import { forwardBackwardLowPass } from '../../utils/spectralAnalysis';

export const TREND_VIEW_LOW_PASS_CUTOFF_HZ = 0.12;
export const TREND_VIEW_SAMPLE_RATE_HZ = 1;

export type TrendProcessingOptions = {
  normalized: boolean;
  lowPassFiltered: boolean;
};

const normalizeTrendSeries = (values: Array<number | undefined>) => {
  const finiteValues = values.filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value)
  );

  if (finiteValues.length === 0) {
    return values.map(() => undefined);
  }

  const mean =
    finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length;
  const variance =
    finiteValues.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
    Math.max(finiteValues.length - 1, 1);
  const std = Math.sqrt(Math.max(variance, 0));

  if (std === 0) {
    return values.map(value => (typeof value === 'number' ? 0 : undefined));
  }

  return values.map(value =>
    typeof value === 'number' && Number.isFinite(value)
      ? (value - mean) / std
      : undefined
  );
};

export const normalizeTrendMap = (valueMap: Map<number, number>) => {
  if (valueMap.size === 0) return valueMap;

  const orderedEntries = [...valueMap.entries()].sort((a, b) => a[0] - b[0]);
  const normalizedValues = normalizeTrendSeries(
    orderedEntries.map(([, value]) => value)
  );

  return new Map(
    orderedEntries.map(([second], index) => [
      second,
      normalizedValues[index] ?? 0,
    ])
  );
};

export const lowPassTrendSeries = (values: number[]) => {
  if (values.length < 3) return values;
  return forwardBackwardLowPass(
    values,
    TREND_VIEW_LOW_PASS_CUTOFF_HZ,
    TREND_VIEW_SAMPLE_RATE_HZ
  );
};

export const lowPassTrendMap = (valueMap: Map<number, number>) => {
  if (valueMap.size < 3) return valueMap;

  const orderedEntries = [...valueMap.entries()].sort((a, b) => a[0] - b[0]);
  const filteredValues = lowPassTrendSeries(
    orderedEntries.map(([, value]) => value)
  );

  return new Map(
    orderedEntries.map(([second], index) => [
      second,
      filteredValues[index] ?? 0,
    ])
  );
};

export const applyTrendProcessingToMap = (
  valueMap: Map<number, number>,
  { normalized, lowPassFiltered }: TrendProcessingOptions
) => {
  let nextMap = valueMap;

  if (normalized) {
    nextMap = normalizeTrendMap(nextMap);
  }

  if (lowPassFiltered) {
    nextMap = lowPassTrendMap(nextMap);
  }

  return nextMap;
};

export const buildTrendMeanLabel = (
  signalLabel: string,
  normalized: boolean
) =>
  normalized ? `Media ${signalLabel} (normalizzato)` : `Media ${signalLabel}`;
